interface FAQ {
  question: string;
  answer: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  keywords?: string[];
  canonical?: string;
  type?: string;
  schema?: Record<string, unknown>;
  breadcrumbItems?: { label: string; href: string }[];
  faq?: FAQ[];
}

const DEFAULT_KEYWORDS = [
  "UK statistics",
  "ONS data",
  "UK data atlas",
  "geospatial visualisation",
  "local authority data",
  "regional statistics",
  "UK charts",
  "interactive map",
  "open data UK",
];

const DEFAULT_SITE_NAME = "MetricMap UK";

export const SEO = ({
  title = "MetricMap UK",
  description = "Interactive geospatial atlas and statistics explorer for official ONS data across the UK.",
  image = "/og-image.png",
  url = "https://metricmap.danielread.co.uk",
  keywords = DEFAULT_KEYWORDS,
  canonical,
  type = "website",
  schema,
  breadcrumbItems,
  faq,
}: SEOProps) => {
  const fullTitle = title === DEFAULT_SITE_NAME ? title : `${title} | ${DEFAULT_SITE_NAME}`;
  // An empty canonical would emit a meaningless href, so only nullish falls through.
  const canonicalUrl = canonical === undefined || canonical === "" ? url : canonical;
  const siteUrl = "https://metricmap.danielread.co.uk";

  const baseSchema: Record<string, unknown>[] = [
    {
      "@type": "SoftwareApplication",
      name: "MetricMap UK",
      applicationCategory: "DataVisualizationApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "GBP",
      },
      description: "Interactive geospatial atlas and statistics explorer for official ONS data across the UK.",
      url: siteUrl,
      image: `${siteUrl}${image}`,
      author: {
        "@type": "Person",
        name: "Dan Read",
      },
    },
    {
      "@type": "WebSite",
      url: siteUrl,
      name: "MetricMap UK",
      description: "Interactive UK National Data Atlas.",
      publisher: {
        "@type": "Person",
        name: "Dan Read",
      },
    },
    {
      "@type": "Organization",
      "@id": `${siteUrl}#organization`,
      name: "MetricMap UK",
      url: siteUrl,
      logo: `${siteUrl}/web-app-manifest-192x192.png`,
      sameAs: [],
    },
  ];

  if (schema) {
    baseSchema.push(schema);
  }

  if (breadcrumbItems && breadcrumbItems.length > 0) {
    baseSchema.push({
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.label,
        item: item.href.startsWith("http") ? item.href : `${siteUrl}${item.href}`,
      })),
    });
  }

  if (faq && faq.length > 0) {
    baseSchema.push({
      "@type": "FAQPage",
      mainEntity: faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  return (
    <>
      {/* Basic Metadata */}
      <title>{fullTitle}</title>
      <meta name='description' content={description} />
      <meta name='keywords' content={keywords.join(", ")} />
      <meta name='author' content='Dan Read' />
      <link rel='canonical' href={canonicalUrl} />

      {/* Social / Open Graph */}
      <meta property='og:type' content={type} />
      <meta property='og:url' content={canonicalUrl} />
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={description} />
      <meta property='og:image' content={`${siteUrl}${image}`} />
      <meta property='og:image:type' content='image/png' />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      <meta property='og:image:alt' content='MetricMap UK — the Union Jack over a UK National Data Atlas card' />
      <meta property='og:site_name' content='MetricMap UK' />
      <meta property='og:locale' content='en_GB' />

      {/* Twitter */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={`${siteUrl}${image}`} />
      <meta name='twitter:creator' content='@danread' />

      {/* PWA / Mobile */}
      <meta name='mobile-web-app-capable' content='yes' />
      <meta name='apple-mobile-web-app-capable' content='yes' />
      <meta name='apple-mobile-web-app-status-bar-style' content='default' />
      <meta name='apple-mobile-web-app-title' content='MetricMap' />
      <meta name='format-detection' content='telephone=no' />

      {/* JSON-LD Structured Data */}
      <script type='application/ld+json'>
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": baseSchema,
        })}
      </script>
    </>
  );
};
